import { useMemo, useRef, useState } from "react";
import clsx from "clsx";
import type { PlaybackFile } from "../../types";

interface Props {
  date: Date;             // dia en visualizacion
  files: PlaybackFile[];
  cursorIso?: string;     // ISO datetime de la posicion actual
  onSeek?: (iso: string) => void;
  selection?: { start: string; end: string } | null;
  onSelectionChange?: (sel: { start: string; end: string } | null) => void;
  // Modo seleccion: cuando esta activo, click+drag normal selecciona un tramo
  // (en vez de hacer seek). Util para el flujo de descarga sin requerir Shift.
  selectMode?: boolean;
  // Callback al terminar una seleccion (mouse up tras drag); el padre puede
  // desactivar el modo seleccion automaticamente.
  onSelectModeEnd?: () => void;
}

function toLocalDahuaIso(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return [
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`,
  ].join("T");
}

function dayBounds(date: Date): { startMs: number; endMs: number } {
  const s = new Date(date);
  s.setHours(0, 0, 0, 0);
  const e = new Date(s);
  e.setDate(e.getDate() + 1);
  return { startMs: s.getTime(), endMs: e.getTime() };
}

function isoInDay(date: Date, fractionOfDay: number): string {
  const { startMs, endMs } = dayBounds(date);
  const ms = startMs + Math.max(0, Math.min(1, fractionOfDay)) * (endMs - startMs);
  return toLocalDahuaIso(new Date(ms));
}

// Fusiona archivos contiguos del mismo tipo en bandas mas grandes. Esto evita
// que cientos de archivos cortos (1-2 min cada uno) se rendericen como 1-2px
// invisibles. Si el gap entre el fin de un archivo y el inicio del siguiente
// es menor a GAP_TOLERANCE_MS, se consideran continuos.
const GAP_TOLERANCE_MS = 60_000;

// Para que un "hueco sin grabacion" se renderice como banda gris, tiene que
// superar este umbral. Si es menor, lo absorbemos en el mergeBands de arriba
// para no llenar el timeline de slivers grises ininteligibles.
const GAP_MIN_VISIBLE_MS = 5 * 60_000;

// Calcula los intervalos del dia donde NO hay grabacion ("gaps") para
// renderizarlos con un fondo gris en el timeline. El operador ve a primer
// vistazo donde hay cobertura y donde no, en lugar de tener que comparar
// bandas verdes contra fondo negro.
function computeGapBands(
  files: PlaybackFile[],
  dayStartMs: number,
  dayEndMs: number,
): { start: number; end: number }[] {
  if (files.length === 0) return [{ start: dayStartMs, end: dayEndMs }];
  const sorted = [...files].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );
  const gaps: { start: number; end: number }[] = [];
  // Bookend antes del primer archivo.
  const firstStart = new Date(sorted[0].start).getTime();
  if (firstStart - dayStartMs > GAP_MIN_VISIBLE_MS) {
    gaps.push({ start: dayStartMs, end: firstStart });
  }
  // Huecos entre archivos consecutivos. Usamos el "end maximo visto" hasta
  // ahora para tolerar archivos solapados (raro en Dahua pero defensivo).
  let prevEnd = new Date(sorted[0].end).getTime();
  for (let i = 1; i < sorted.length; i++) {
    const fs = new Date(sorted[i].start).getTime();
    const fe = new Date(sorted[i].end).getTime();
    if (fs - prevEnd > GAP_MIN_VISIBLE_MS) {
      gaps.push({ start: prevEnd, end: fs });
    }
    if (fe > prevEnd) prevEnd = fe;
  }
  // Bookend despues del ultimo archivo.
  if (dayEndMs - prevEnd > GAP_MIN_VISIBLE_MS) {
    gaps.push({ start: prevEnd, end: dayEndMs });
  }
  return gaps;
}

function mergeBands(files: PlaybackFile[]): { start: number; end: number; type: string }[] {
  if (files.length === 0) return [];
  const sorted = [...files].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );
  const bands: { start: number; end: number; type: string }[] = [];
  let cur = {
    start: new Date(sorted[0].start).getTime(),
    end: new Date(sorted[0].end).getTime(),
    type: sorted[0].type,
  };
  for (let i = 1; i < sorted.length; i++) {
    const fs = new Date(sorted[i].start).getTime();
    const fe = new Date(sorted[i].end).getTime();
    if (fs - cur.end <= GAP_TOLERANCE_MS && sorted[i].type === cur.type) {
      cur.end = Math.max(cur.end, fe);
    } else {
      bands.push(cur);
      cur = { start: fs, end: fe, type: sorted[i].type };
    }
  }
  bands.push(cur);
  return bands;
}

export function Timeline({
  date, files, cursorIso, onSeek, selection, onSelectionChange,
  selectMode = false, onSelectModeEnd,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);

  const { startMs, endMs } = dayBounds(date);
  const totalMs = endMs - startMs;

  // Pre-procesamos: archivos -> bandas fusionadas, luego cada banda -> %
  const bands = useMemo(() => {
    const merged = mergeBands(files);
    return merged.map((b, i) => {
      const left = ((Math.max(b.start, startMs) - startMs) / totalMs) * 100;
      const width = ((Math.min(b.end, endMs) - Math.max(b.start, startMs)) / totalMs) * 100;
      return { key: i, left, width: Math.max(0, width), type: b.type };
    }).filter((b) => b.width > 0);
  }, [files, startMs, endMs, totalMs]);

  // Intervalos sin grabacion (gap bands en gris claro). Calculados igual que
  // mergeBands pero en negativo. Se renderizan DEBAJO de las bandas activas
  // (verde/rojo) — donde haya banda activa, ningun gap quedaria visible.
  const gapBands = useMemo(() => {
    const gaps = computeGapBands(files, startMs, endMs);
    return gaps.map((g, i) => {
      const left = ((Math.max(g.start, startMs) - startMs) / totalMs) * 100;
      const width = ((Math.min(g.end, endMs) - Math.max(g.start, startMs)) / totalMs) * 100;
      return { key: i, left, width: Math.max(0, width), startMs: g.start, endMs: g.end };
    }).filter((b) => b.width > 0);
  }, [files, startMs, endMs, totalMs]);

  const cursorPct = cursorIso
    ? Math.max(0, Math.min(100, ((new Date(cursorIso).getTime() - startMs) / totalMs) * 100))
    : null;

  const xToPct = (clientX: number): number => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return 0;
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    return x / rect.width;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const f = xToPct(e.clientX);
    // selectMode (toggle UI) o Shift (atajo legacy) activan seleccion en lugar de seek.
    if (e.shiftKey || selectMode) {
      setDragStart(f);
      onSelectionChange?.({ start: isoInDay(date, f), end: isoInDay(date, f) });
    } else {
      onSeek?.(isoInDay(date, f));
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const f = xToPct(e.clientX);
    setHover(f);
    if (dragStart != null) {
      const a = Math.min(dragStart, f);
      const b = Math.max(dragStart, f);
      onSelectionChange?.({ start: isoInDay(date, a), end: isoInDay(date, b) });
    }
  };

  const handleMouseUp = () => {
    if (dragStart != null) {
      // Al terminar el drag, salir del modo seleccion para que el siguiente
      // click vuelva a hacer seek normal.
      onSelectModeEnd?.();
    }
    setDragStart(null);
  };

  // Tooltip de hover: muestra HH:MM:SS cuando el mouse esta sobre la timeline.
  // Si el cursor cae sobre un gap (intervalo sin grabacion), incluye "Sin grabacion".
  const hoverTime = useMemo(() => {
    if (hover == null) return null;
    return isoInDay(date, hover).slice(11, 19);
  }, [hover, date]);
  const hoverOverGap = useMemo(() => {
    if (hover == null) return false;
    const hoverMs = startMs + hover * totalMs;
    return gapBands.some((g) => hoverMs >= g.startMs && hoverMs < g.endMs);
  }, [hover, startMs, totalMs, gapBands]);

  return (
    <div className="bg-panel border-t border-line">
      {/* Header con etiquetas de hora */}
      <div className="flex h-5 text-xs text-muted relative px-1 select-none">
        {Array.from({ length: 24 }).map((_, h) => (
          <div key={h} style={{ width: `${100 / 24}%` }} className="border-r border-line text-center">
            {h.toString().padStart(2, "0")}
          </div>
        ))}
      </div>

      {/* Track principal */}
      <div
        ref={ref}
        className={clsx(
          "relative h-12",
          selectMode ? "cursor-crosshair" : "cursor-pointer",
        )}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => { setHover(null); setDragStart(null); }}
        onMouseUp={handleMouseUp}
      >
        {/* Grilla vertical horaria */}
        <div className="absolute inset-0 grid grid-cols-24 pointer-events-none">
          {Array.from({ length: 24 }).map((_, h) => (
            <div key={h} className="border-r border-line/50 h-full" />
          ))}
        </div>

        {/* Bandas grises de "sin grabacion": fondo del track para zonas donde
            no hay archivos. Se renderizan PRIMERO para que las bandas activas
            verdes/rojas las cubran si hay solapamiento accidental. */}
        {gapBands.map((g) => (
          <div
            key={`gap-${g.key}`}
            className="absolute top-2 h-8 bg-line/40"
            style={{ left: `${g.left}%`, width: `${g.width}%` }}
            title="Sin grabacion"
          />
        ))}

        {/* Bandas de grabacion (verde = regular, rojo = alarma).
            minWidth: 3px asegura visibilidad aun para tramos cortos. */}
        {bands.map((b) => (
          <div
            key={b.key}
            className={clsx(
              "absolute top-2 h-8 rounded-sm",
              b.type === "alarm" ? "bg-red-700/70" : "bg-green-700/70",
            )}
            style={{
              left: `${b.left}%`,
              width: `${b.width}%`,
              minWidth: "3px",
            }}
          />
        ))}

        {/* Seleccion activa (Shift+drag o selectMode) */}
        {selection && (
          <div
            className="absolute top-0 bottom-0 bg-accent/30 border-x-2 border-accent pointer-events-none"
            style={{
              left: `${((new Date(selection.start).getTime() - startMs) / totalMs) * 100}%`,
              width: `${((new Date(selection.end).getTime() - new Date(selection.start).getTime()) / totalMs) * 100}%`,
            }}
          />
        )}

        {/* Cursor de reproduccion */}
        {cursorPct != null && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-accent pointer-events-none"
            style={{ left: `${cursorPct}%` }}
          />
        )}

        {/* Hover guide */}
        {hover != null && (
          <>
            <div
              className="absolute top-0 bottom-0 w-px bg-white/30 pointer-events-none"
              style={{ left: `${hover * 100}%` }}
            />
            <div
              className="absolute -top-5 text-[10px] text-white bg-black/70 px-1 rounded pointer-events-none whitespace-nowrap"
              style={{ left: `${hover * 100}%`, transform: "translateX(-50%)" }}
            >
              {hoverTime}{hoverOverGap ? " — sin grabacion" : ""}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
